import React from "react";
import { Link } from "@/lib/router-compat";
import {
  Download,
  Printer,
  ArrowRight,
  Award,
  Clock,
  TrendingUp,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const COMPARISON_IMAGE_URL =
  "https://vibe.filesafe.space/1787327131284421342/attachments/62b4a6ae-016a-47f4-af4a-f13d423d5dfd.png";

const ARTICLE_URL = "/how-saint-compares-to-other-depression-treatment-options";

const handlePrint = () => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    window.print();
    return;
  }
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>How SAINT Compares to Other Depression Treatment Options - Neuro Wellness</title>
        <style>
          body { margin: 0; padding: 20px; font-family: sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #fff; }
          img { max-width: 100%; height: auto; max-height: 95vh; object-fit: contain; }
          @media print { body { padding: 0; } img { width: 100%; max-height: 100vh; } }
        </style>
      </head>
      <body>
        <img src="${COMPARISON_IMAGE_URL}" alt="How SAINT Compares to Other Depression Treatment Options" onload="window.print(); window.close();" />
      </body>
    </html>
  `);
  printWindow.document.close();
};

/* ────────────────────────────────────────────────────────────
   VARIANT 1 — Full inline comparison table + download card
   Used on the main comparison article page.
   ──────────────────────────────────────────────────────────── */
export const SaintComparisonFull: React.FC = () => {
  const metrics = [
    {
      metric: "Remission rate",
      saint: "79–90%",
      ect: "48%",
      tms: "17–32%",
      medication: "14%",
    },
    {
      metric: "Average time to remission",
      saint: "2.6 days",
      ect: "3 weeks",
      tms: "3.5 weeks",
      medication: "6–12 weeks",
    },
  ];
  const treatments = ["SAINT®", "ECT", "Standard TMS", "Medication"];

  return (
    <div className="space-y-6 not-prose">
      {/* Desktop table (md and up) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm text-left border-collapse rounded-xl overflow-hidden shadow-sm border border-border">
          <thead>
            <tr className="bg-primary text-white">
              <th className="py-3.5 px-4 font-semibold text-xs uppercase tracking-wider">
                Treatment Metric
              </th>
              <th className="py-3.5 px-4 font-extrabold text-sm text-center bg-accent text-accent-foreground border-x border-accent-foreground/20">
                SAINT®
              </th>
              <th className="py-3.5 px-4 font-semibold text-xs text-center uppercase tracking-wider">
                ECT
              </th>
              <th className="py-3.5 px-4 font-semibold text-xs text-center uppercase tracking-wider">
                Standard TMS
              </th>
              <th className="py-3.5 px-4 font-semibold text-xs text-center uppercase tracking-wider">
                Medication
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-card">
            {metrics.map((row) => (
              <tr
                key={row.metric}
                className="hover:bg-muted/40 transition-colors"
              >
                <td className="py-4 px-4 font-bold text-primary">
                  {row.metric}
                </td>
                <td className="py-4 px-4 text-center font-extrabold text-lg text-accent bg-accent/10 border-x border-accent/20">
                  {row.saint}
                </td>
                <td className="py-4 px-4 text-center font-semibold text-foreground">
                  {row.ect}
                </td>
                <td className="py-4 px-4 text-center text-foreground">
                  {row.tms}
                </td>
                <td className="py-4 px-4 text-center text-foreground">
                  {row.medication}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card layout (below md) */}
      <div className="md:hidden space-y-4">
        {metrics.map((row) => (
          <div
            key={row.metric}
            className="rounded-xl border border-border bg-card shadow-soft overflow-hidden"
          >
            <div className="bg-primary px-4 py-3">
              <p className="font-headings text-sm font-bold text-white">
                {row.metric}
              </p>
            </div>
            <div className="divide-y divide-border">
              {treatments.map((treat, i) => {
                const values = [row.saint, row.ect, row.tms, row.medication];
                const isSaint = i === 0;
                return (
                  <div
                    key={treat}
                    className={`flex items-center justify-between px-4 py-3 ${
                      isSaint ? "bg-accent/10" : ""
                    }`}
                  >
                    <span
                      className={`text-sm font-semibold ${
                        isSaint ? "text-accent" : "text-foreground/70"
                      }`}
                    >
                      {treat}
                    </span>
                    <span
                      className={`text-sm font-bold ${
                        isSaint ? "text-accent text-base" : "text-foreground"
                      }`}
                    >
                      {values[i]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground italic">
        Study designs and populations vary across these figures, and individual
        results vary. A consultation is required to determine whether any of
        these treatments is appropriate for you.
      </p>

      <div className="mt-6 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/10 p-5 sm:p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <a
            href={COMPARISON_IMAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 group relative overflow-hidden rounded-lg shadow-md border border-border hover:shadow-xl transition-all"
          >
            <img
              src={COMPARISON_IMAGE_URL}
              alt="How SAINT Compares to Other Depression Treatment Options Chart"
              className="w-48 sm:w-56 h-auto object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-primary/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold uppercase tracking-wider">
              Click to Enlarge
            </div>
          </a>
          <div className="space-y-3 flex-1 text-center md:text-left">
            <span className="text-xs font-bold uppercase tracking-wider text-accent block">
              Downloadable Patient Resource
            </span>
            <h3 className="text-lg sm:text-xl font-headings font-bold text-primary">
              SAINT® Comparison Handout (Print & Save)
            </h3>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Download or print this full side-by-side comparison chart to bring
              to your next medical appointment, share with your prescribing
              doctor, or discuss with your family.
            </p>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 pt-1">
              <Button
                asChild
                className="bg-gradient-accent text-accent-foreground font-semibold shadow-sm hover:opacity-90"
              >
                <a
                  href={COMPARISON_IMAGE_URL}
                  download="SAINT-Depression-Treatment-Comparison.png"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Chart
                </a>
              </Button>
              <Button
                variant="outline"
                onClick={handlePrint}
                className="border-primary/30 text-primary hover:bg-primary/5 font-semibold"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Handout
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────
   VARIANT 2 — Stat band (horizontal highlight cards)
   Compact, eye-catching. Good for mid-article placement.
   ──────────────────────────────────────────────────────────── */
export const SaintComparisonStatBand: React.FC = () => (
  <div className="not-prose my-8">
    <div className="rounded-2xl border border-border bg-card shadow-soft overflow-hidden">
      <div className="bg-primary px-5 py-3 flex items-center justify-between">
        <span className="text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-accent" />
          How SAINT® Compares
        </span>
        <Link
          to={ARTICLE_URL}
          className="text-accent text-xs font-semibold hover:underline flex items-center gap-1"
        >
          Full comparison <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-border">
        <div className="p-4 text-center bg-accent/5">
          <div className="text-2xl sm:text-3xl font-extrabold text-accent">
            79–90%
          </div>
          <div className="text-xs text-muted-foreground mt-1 leading-tight">
            SAINT® remission rate
          </div>
        </div>
        <div className="p-4 text-center">
          <div className="text-2xl sm:text-3xl font-extrabold text-primary">
            2.6 days
          </div>
          <div className="text-xs text-muted-foreground mt-1 leading-tight">
            Avg. time to remission
          </div>
        </div>
        <div className="p-4 text-center">
          <div className="text-2xl sm:text-3xl font-extrabold text-primary">
            5 days
          </div>
          <div className="text-xs text-muted-foreground mt-1 leading-tight">
            vs. 30–36 visits (standard TMS)
          </div>
        </div>
        <div className="p-4 text-center">
          <div className="text-2xl sm:text-3xl font-extrabold text-primary">
            14%
          </div>
          <div className="text-xs text-muted-foreground mt-1 leading-tight">
            Medication remission rate
          </div>
        </div>
      </div>
      <div className="px-5 py-3 bg-muted/30 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground italic">
          Data from published trials in treatment-resistant depression.
          Individual results vary.
        </p>
        <div className="flex items-center gap-2">
          <Button
            asChild
            size="sm"
            className="bg-gradient-accent text-accent-foreground font-semibold text-xs"
          >
            <a
              href={COMPARISON_IMAGE_URL}
              download="SAINT-Depression-Treatment-Comparison.png"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Download Chart
            </a>
          </Button>
          <Button
            asChild
            size="sm"
            variant="outline"
            className="border-primary/30 text-primary text-xs font-semibold"
          >
            <Link to={ARTICLE_URL}>
              Read Full Article <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  </div>
);

/* ────────────────────────────────────────────────────────────
   VARIANT 3 — Compact feature card with mini-table
   Single-column, minimal. Good for articles that already
   have heavy content and need a lighter touch.
   ──────────────────────────────────────────────────────────── */
export const SaintComparisonCompactCard: React.FC = () => (
  <div className="not-prose my-8">
    <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 p-5 sm:p-6 shadow-soft">
      <div className="flex items-center gap-2 mb-4">
        <Award className="h-5 w-5 text-accent" />
        <h3 className="font-headings text-lg font-bold text-primary">
          SAINT® vs. Other Depression Treatments
        </h3>
      </div>

      <div className="overflow-x-auto -mx-2 sm:mx-0 mb-4">
        <table className="w-full min-w-[420px] text-xs sm:text-sm text-left border-collapse rounded-lg overflow-hidden border border-border">
          <thead>
            <tr className="bg-primary text-white">
              <th className="py-2.5 px-3 font-semibold">Treatment</th>
              <th className="py-2.5 px-3 font-bold text-center bg-accent text-accent-foreground">
                SAINT®
              </th>
              <th className="py-2.5 px-3 font-medium text-center">ECT</th>
              <th className="py-2.5 px-3 font-medium text-center">TMS</th>
              <th className="py-2.5 px-3 font-medium text-center">
                Medication
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-background">
            <tr>
              <td className="py-2.5 px-3 font-bold text-primary">Remission</td>
              <td className="py-2.5 px-3 text-center font-extrabold text-accent bg-accent/10">
                79–90%
              </td>
              <td className="py-2.5 px-3 text-center">48%</td>
              <td className="py-2.5 px-3 text-center">17–32%</td>
              <td className="py-2.5 px-3 text-center">14%</td>
            </tr>
            <tr>
              <td className="py-2.5 px-3 font-bold text-primary">Time</td>
              <td className="py-2.5 px-3 text-center font-extrabold text-accent bg-accent/10">
                2.6 days
              </td>
              <td className="py-2.5 px-3 text-center">3 wks</td>
              <td className="py-2.5 px-3 text-center">3.5 wks</td>
              <td className="py-2.5 px-3 text-center">6–12 wks</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex items-start gap-2 mb-4">
        <ShieldCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />
        <p className="text-xs text-foreground/80 leading-relaxed">
          SAINT® uses personalized fMRI targeting and compresses treatment into
          5 days — the highest remission rate, in the shortest time, without
          anesthesia. Individual results vary.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          asChild
          size="sm"
          className="bg-gradient-accent text-accent-foreground font-semibold text-xs"
        >
          <Link to={ARTICLE_URL}>
            See Full Comparison <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Link>
        </Button>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="border-primary/30 text-primary text-xs font-semibold"
        >
          <a
            href={COMPARISON_IMAGE_URL}
            download="SAINT-Depression-Treatment-Comparison.png"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Download Handout
          </a>
        </Button>
      </div>
    </div>
  </div>
);

/* ────────────────────────────────────────────────────────────
   VARIANT 4 — Split visual + stats (image-forward)
   Shows the comparison image prominently with key stats
   alongside. Good for articles with visual emphasis.
   ──────────────────────────────────────────────────────────── */
export const SaintComparisonVisualSplit: React.FC = () => (
  <div className="not-prose my-8">
    <div className="rounded-2xl border border-border bg-card shadow-soft overflow-hidden grid sm:grid-cols-2 gap-0">
      {/* Image side */}
      <a
        href={COMPARISON_IMAGE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="relative group block overflow-hidden bg-muted/20"
      >
        <img
          src={COMPARISON_IMAGE_URL}
          alt="How SAINT Compares to Other Depression Treatment Options"
          className="w-full h-full object-cover min-h-[200px] group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-primary/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold uppercase tracking-wider">
          Click to Enlarge
        </div>
      </a>

      {/* Stats side */}
      <div className="p-5 sm:p-6 flex flex-col justify-between">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/15 text-accent text-xs font-bold uppercase tracking-wider mb-3">
            <Clock className="h-3.5 w-3.5" />
            Side-by-Side
          </div>
          <h3 className="font-headings text-lg font-bold text-primary mb-3">
            See How SAINT® Stacks Up
          </h3>
          <div className="space-y-2.5">
            <div className="flex items-baseline justify-between border-b border-border pb-2">
              <span className="text-sm text-foreground/80">
                SAINT® remission
              </span>
              <span className="text-lg font-extrabold text-accent">79–90%</span>
            </div>
            <div className="flex items-baseline justify-between border-b border-border pb-2">
              <span className="text-sm text-foreground/80">
                Time to remission
              </span>
              <span className="text-lg font-extrabold text-primary">
                2.6 days
              </span>
            </div>
            <div className="flex items-baseline justify-between border-b border-border pb-2">
              <span className="text-sm text-foreground/80">
                Medication remission
              </span>
              <span className="text-lg font-extrabold text-muted-foreground">
                14%
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button
            asChild
            size="sm"
            className="bg-gradient-accent text-accent-foreground font-semibold text-xs"
          >
            <Link to={ARTICLE_URL}>
              Read Full Comparison <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Link>
          </Button>
          <Button
            asChild
            size="sm"
            variant="outline"
            className="border-primary/30 text-primary text-xs font-semibold"
          >
            <a
              href={COMPARISON_IMAGE_URL}
              download="SAINT-Depression-Treatment-Comparison.png"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Download
            </a>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrint}
            className="text-primary text-xs font-semibold"
          >
            <Printer className="h-3.5 w-3.5 mr-1.5" />
            Print
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export default SaintComparisonFull;
